import FroalaEditor from "froala-editor";

const normalizeStyleDefinitions = (styles) => {
    if (!styles) return [];

    if (!Array.isArray(styles) && typeof styles === "object") {
        return Object.keys(styles).map((key) => {
            const item = styles[key];

            if (typeof item === "string") {
                return {
                    value: key,
                    label: item,
                    className: key
                };
            }

            return {
                value: key,
                label: item.label || key,
                className: item.className || item.class || key
            };
        });
    }

    if (Array.isArray(styles)) {
        return styles.map((item) => ({
            value: item.value,
            label: item.label || item.value,
            className: item.className || item.class || item.value
        }));
    }

    return [];
};

const getClosestTag = (editor, allowedTags = []) => {
    const el = editor.selection.element();
    if (!el) return null;

    let node = el;
    while (node && node.nodeType === 1) {
        if (allowedTags.includes(node.tagName)) {
            return node;
        }
        node = node.parentElement;
    }

    return null;
};

const removeClasses = (el, classNames = []) => {
    if (!el) return;
    classNames.forEach((className) => className && el.classList.remove(className));
};

const convertListTag = (list, newTagName) => {
    if (!list || list.tagName === newTagName) return list;

    const newList = document.createElement(newTagName.toLowerCase());

    [...list.attributes].forEach((attr) => {
        if (attr.name !== "type") {
            newList.setAttribute(attr.name, attr.value);
        }
    });

    while (list.firstChild) {
        newList.appendChild(list.firstChild);
    }

    list.parentNode.replaceChild(newList, list);

    return newList;
};

const ensureList = (editor, tagName) => {
    let list = getClosestTag(editor, ["UL", "OL"]);

    if (list) {
        if (list.tagName !== tagName) {
            list = convertListTag(list, tagName);
        }
        return list;
    }

    editor.selection.restore();

    if (tagName === "UL") {
        editor.commands.exec("formatUL");
    } else {
        editor.commands.exec("formatOL");
    }

    list = getClosestTag(editor, [tagName]);

    if (!list) {
        const lists = editor.el.querySelectorAll(tagName.toLowerCase());
        if (lists.length) {
            list = lists[lists.length - 1];
        }
    }

    return list;
};

const getUlDefinitions = (editor) => normalizeStyleDefinitions(editor.opts.listUnorderedStyles || {});
const getOlDefinitions = (editor) => normalizeStyleDefinitions(editor.opts.listOrderedStyles || {});

const buildDropdownHtml = (definitions, commandName) => {
    return `
        <ul class="fr-dropdown-list" role="presentation">
            ${definitions.map((item) => `
                <li role="presentation">
                    <a
                        class="fr-command"
                        data-cmd="${commandName}"
                        data-param1="${item.value}"
                        title="${item.label}"
                        href="#"
                    >
                        ${item.label}
                    </a>
                </li>
            `).join("")}
        </ul>
    `;
};

export default function registerCustomListStyles(props) {
    FroalaEditor.DefineIconTemplate('custom_svg', '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">[PATH]</svg>');

    if (!FroalaEditor.COMMANDS.ulStyle) {
        FroalaEditor.DefineIcon('ulStyle', {
            template: 'custom_svg',
            viewBox: '0 0 24 24',
            PATH: `
                <g id="Layer_1-2" data-name="Layer 1">
                    <g id="bulleted-list-star">
                        <path d="M2.81,0H.94C.42,0,0,.42,0,.94v1.87c0,.52.42.94.94.94h1.87c.52,0,.94-.42.94-.94V.94c0-.52-.42-.94-.94-.94ZM18.14.63H6.88c-.69,0-1.25.56-1.25,1.25s.55,1.25,1.25,1.25h11.27c.69,0,1.25-.55,1.25-1.25s-.56-1.25-1.25-1.25ZM6.88,6.89c-.69,0-1.25.55-1.25,1.25s.55,1.25,1.25,1.25h6.81l1.27-2.5H6.88ZM11.1,15.19l-1.9-1.91s-.09-.09-.12-.14h-2.21c-.69,0-1.25.56-1.25,1.25s.55,1.25,1.25,1.25h4.68l-.45-.46ZM2.81,6.26H.94c-.52,0-.94.42-.94.94v1.87c0,.52.42.94.94.94h1.87c.52,0,.94-.42.94-.94v-1.87c0-.52-.42-.94-.94-.94ZM2.81,12.51H.94c-.52,0-.94.42-.94.94v1.88c0,.52.42.94.94.94h1.87c.52,0,.94-.42.94-.94v-1.88c0-.52-.42-.94-.94-.94Z"/>
                        <path d="M23.82,12.31l-2.92,2.93.64,4.06c.04.24-.05.46-.23.59-.11.07-.24.13-.37.13-.1,0-.18-.03-.28-.07l-3.67-1.86-3.66,1.86c-.1.04-.18.07-.28.07-.13,0-.26-.06-.37-.13-.18-.13-.27-.35-.24-.59l.59-3.66.06-.4-1.02-1.02-1.08-1.08-.82-.83c-.11-.11-.18-.27-.18-.41,0-.07.02-.13.04-.2.07-.23.26-.39.47-.43l1.57-.24,2.52-.4.63-1.25,1.25-2.43s.02-.05.05-.07c.1-.16.3-.27.48-.27.21,0,.39.1.51.27.02.02.03.05.05.07l1.16,2.29.71,1.38,4.07.64c.22.04.41.2.48.43.07.22.02.46-.14.61Z"/>
                    </g>
                </g>
            `
        });

        FroalaEditor.RegisterCommand("ulStyle", {
            title: props.intl.formatMessage({ id: 'CMS.Froala.plugin.list_style.ul.title' }),
            type: "dropdown",
            focus: true,
            undo: true,
            refreshAfterCallback: true,
            html: function () {
                return buildDropdownHtml(getUlDefinitions(this), "ulStyle");
            },
            callback: function (cmd, val) {
                const defs = getUlDefinitions(this);
                const selected = defs.find((item) => item.value === val);
                if (!selected) return;

                this.selection.save();
                this.events.focus(true);

                const list = ensureList(this, "UL");
                if (!list) return;

                removeClasses(list, defs.map((item) => item.className));
                list.removeAttribute("type");
                list.classList.add(selected.className);

                this.selection.restore();
                this.undo.saveStep();
                this.events.trigger("contentChanged");
                this.$el.trigger("input");
            },
            refresh: function ($btn) {
                if (!getUlDefinitions(this).length) {
                    $btn.addClass("fr-hidden");
                } else {
                    $btn.removeClass("fr-hidden");
                }
            }
        });
    }

    if (!FroalaEditor.COMMANDS.olStyle) {
        FroalaEditor.DefineIcon('olStyle', {
            template: 'custom_svg',
            viewBox: '0 0 24 24',
            PATH: `
                <g xmlns="http://www.w3.org/2000/svg" id="Layer_1-2" data-name="Layer 1">
                    <g id="numbered-list-star">
                    <path d="M6.88,9.76h6.81l1.27-2.5H6.88c-.69,0-1.25.55-1.25,1.25s.55,1.25,1.25,1.25Z"/>
                    <path d="M6.88,3.5h11.27c.69,0,1.25-.55,1.25-1.25s-.56-1.25-1.25-1.25H6.88c-.69,0-1.25.56-1.25,1.25s.55,1.25,1.25,1.25Z"/>
                    <path d="M9.21,13.65s-.09-.09-.12-.14h-2.21c-.69,0-1.25.56-1.25,1.25s.55,1.25,1.25,1.25h4.68l-.45-.46-1.9-1.91Z"/>
                    <path d="M23.97,12.07c-.07-.23-.26-.39-.48-.43l-4.07-.64-.71-1.38-1.16-2.29s-.03-.05-.05-.07c-.12-.16-.3-.27-.51-.27-.18,0-.39.1-.48.27-.02.02-.04.05-.05.07l-1.25,2.43-.63,1.25-2.52.4-1.57.24c-.22.04-.4.2-.47.43-.02.07-.04.13-.04.2,0,.14.07.3.18.41l.82.83,1.08,1.08,1.02,1.02-.06.4-.59,3.66c-.03.24.06.46.24.59.11.07.24.13.37.13.1,0,.18-.03.28-.07l3.66-1.86,3.67,1.86c.1.04.18.07.28.07.13,0,.26-.06.37-.13.18-.13.27-.35.23-.59l-.64-4.06,2.92-2.93c.16-.15.22-.39.14-.61Z"/>
                    <path d="M3.07,3.27h-.61V.61c0-.34-.27-.61-.61-.61H.61C.27,0,0,.28,0,.61s.27.61.61.61h.61v2.04h-.61C.27,3.27,0,3.54,0,3.88s.27.61.61.61h2.45c.34,0,.61-.27.61-.61s-.27-.61-.61-.61Z"/>
                    <path d="M3.07,9.53h-.85l.61-.45c.4-.29.64-.76.64-1.26,0-.86-.7-1.56-1.56-1.56h-.13c-.37,0-.72.12-1.01.34l-.53.4c-.27.2-.33.59-.12.86.2.27.59.33.86.12l.53-.4c.08-.06.18-.09.28-.09h.13c.18,0,.33.15.33.33,0,.11-.05.21-.14.27l-1.8,1.31C.12,9.54,0,9.77,0,10.01c0,.41.33.75.75.75h2.32c.34,0,.61-.27.61-.61s-.27-.61-.61-.61Z"/>
                    <path d="M2.24,12.51H.63c-.34,0-.61.27-.61.61s.27.61.61.61h1.62c.12,0,.21.09.21.21s-.09.21-.21.21h-.85c-.34,0-.61.27-.61.61s.27.61.61.61h.85c.12,0,.21.09.21.21s-.09.21-.21.21H.63c-.34,0-.61.27-.61.61s.27.61.61.61h1.61c.79,0,1.43-.64,1.43-1.43,0-.3-.1-.59-.26-.82.16-.23.26-.51.26-.82,0-.79-.64-1.43-1.43-1.43Z"/>
                    </g>
                </g>
            `
        });

        FroalaEditor.RegisterCommand("olStyle", {
            title: props.intl.formatMessage({ id: 'CMS.Froala.plugin.list_style.ol.title' }),
            type: "dropdown",
            focus: true,
            undo: true,
            refreshAfterCallback: true,
            html: function () {
                return buildDropdownHtml(getOlDefinitions(this), "olStyle");
            },
            callback: function (cmd, val) {
                const defs = getOlDefinitions(this);
                const selected = defs.find((item) => item.value === val);
                if (!selected) return;

                this.selection.save();
                this.events.focus(true);

                const list = ensureList(this, "OL");
                if (!list) return;

                removeClasses(list, defs.map((item) => item.className));
                list.removeAttribute("type");
                list.classList.add(selected.className);

                this.selection.restore();
                this.undo.saveStep();
                this.events.trigger("contentChanged");
                this.$el.trigger("input");
            },
            refresh: function ($btn) {
                if (!getOlDefinitions(this).length) {
                    $btn.addClass("fr-hidden");
                } else {
                    $btn.removeClass("fr-hidden");
                }
            }
        });
    }
}